import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const NORM = 125;
// More front-on camera angle
const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: -0.72, y: 0.60, z: -1.19 } : { x: -0.77, y: 0.63, z: -1.26 };

/**
 * Build a soft bolster cushion geometry — a cylinder with rounded/bulging
 * ends and a slightly puffy body, so it looks like a real cushion.
 */
function buildBolsterGeo(radius, length, radialSegs = 64, lengthSegs = 32) {
  const r = Math.max(radius, 0.001);
  const l = Math.max(length, 0.001);
  const hl = l / 2;

  // Bulge factor — no puffiness, straight body
  const bulgeFactor = 0.0;
  // End rounding radius as fraction of radius
  const endRound = r * 0.33;

  const positions = [];
  const uvs = [];
  const indices = [];

  // Build a profile: a series of rings from left cap center → left cap edge → body → right cap edge → right cap center
  // Total sections: cap hemisphere left + body + cap hemisphere right
  const capSegs = 8; // segments for the rounded cap
  const totalLengthSegs = capSegs + lengthSegs + capSegs;

  for (let j = 0; j <= totalLengthSegs; j++) {
    const t = j / totalLengthSegs;

    let x, ringRadius;

    if (j <= capSegs) {
      // Left cap — rounded hemisphere
      const capT = j / capSegs; // 0 = center, 1 = edge
      const angle = (1 - capT) * Math.PI / 2; // PI/2 at center → 0 at edge
      x = -hl - Math.cos(angle) * endRound + endRound;
      ringRadius = r * Math.sin(angle === Math.PI / 2 ? Math.PI / 2 : Math.PI / 2) * capT;
      // Smoother: use sin for radius growth
      ringRadius = r * Math.sin(capT * Math.PI / 2);
      x = -hl + endRound * (1 - Math.cos(capT * Math.PI / 2));
    } else if (j >= totalLengthSegs - capSegs) {
      // Right cap — rounded hemisphere
      const capT = (totalLengthSegs - j) / capSegs; // 1 = edge, 0 = center
      ringRadius = r * Math.sin(capT * Math.PI / 2);
      x = hl - endRound * (1 - Math.cos(capT * Math.PI / 2));
    } else {
      // Body section
      const bodyT = (j - capSegs) / lengthSegs; // 0 to 1 along body
      x = -hl + endRound + bodyT * (l - 2 * endRound);
      // Slight belly bulge in the middle
      const bulge = 1 + bulgeFactor * Math.sin(bodyT * Math.PI);
      ringRadius = r * bulge;
    }

    for (let i = 0; i <= radialSegs; i++) {
      const theta = (i / radialSegs) * Math.PI * 2;
      const y = Math.cos(theta) * ringRadius;
      const z = Math.sin(theta) * ringRadius;

      positions.push(x, y, z);

      // World-space UV: tile at consistent physical scale
      const TILE_INV = 4; // 1/0.25m → one pattern tile per 25cm
      const u = x * TILE_INV;
      const v = y * TILE_INV;
      uvs.push(u, v);
    }
  }

  // Build indices
  for (let j = 0; j < totalLengthSegs; j++) {
    for (let i = 0; i < radialSegs; i++) {
      const a = j * (radialSegs + 1) + i;
      const b = a + 1;
      const c = a + (radialSegs + 1);
      const d = c + 1;
      indices.push(a, b, d);
      indices.push(a, d, c);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

const BolsterCushion3DPreview = ({
  diameter = 20, length = 70,
  fabricColor, fabricImageUrl, fabricZoom = 1,
  showZip = true,
  rawDiameter = 0, rawLength = 0, unit = 'cm',
  activeField = null,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const mountRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const bodyRef = useRef(null);
  const zipRef = useRef(null);
  const diamLineRef = useRef(null);
  const dimLabelsRef = useRef({ diameter: null, length: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const containerSizeRef = useRef({ w: 0, h: 0 });
  const initializedRef = useRef(false);
  const animFrameRef = useRef(null);
  const resetCameraRef = useRef(true);
  const showZipRef = useRef(showZip);
  const activeFieldRef = useRef(activeField);
  const pipingLeftRef = useRef(null);
  const pipingRightRef = useRef(null);

  useEffect(() => { showZipRef.current = showZip; }, [showZip]);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  useEffect(() => {
    if (!mountRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = mountRef.current;
    const cw = container.clientWidth, ch = container.clientHeight;
    containerSizeRef.current = { w: cw, h: ch };

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f0eb);

    const camera = new THREE.PerspectiveCamera(35, cw / ch, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(cw, ch);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dir1.position.set(3, 5, 3);
    dir1.castShadow = true;
    dir1.shadow.mapSize.set(1024, 1024);
    scene.add(dir1);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Shadow floor
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.05;
    floor.receiveShadow = true;
    scene.add(floor);

    // Initial dimensions
    const r0 = (diameter / NORM) / 2;
    const l0 = length / NORM;
    const mat = new THREE.MeshStandardMaterial({
      color: 0xc8b89a, roughness: 0.85, metalness: 0.02,
      side: THREE.DoubleSide,
      polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1
    });

    const geo = buildBolsterGeo(r0, l0);
    const body = new THREE.Mesh(geo, mat);
    body.castShadow = true;
    scene.add(body);
    bodyRef.current = body;

    currentDimsRef.current = { r: r0, l: l0 };
    targetDimsRef.current = { r: r0, l: l0 };

    // Zip along the bottom of the cylinder — white line + teeth
    const zipTapeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const zipTeethMat = new THREE.MeshBasicMaterial({ color: 0x888888 });
    const zipTube = new THREE.Mesh(new THREE.BufferGeometry(), zipTapeMat);
    scene.add(zipTube);
    const zipTeeth = Array.from({ length: 80 }, () => {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.002, 0.003), zipTeethMat);
      scene.add(tooth);
      return tooth;
    });
    const zipPull = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.004, 0.004), zipTeethMat);
    scene.add(zipPull);
    zipRef.current = { tube: zipTube, teeth: zipTeeth, pull: zipPull };

    // Diameter dimension line — T-cushion style: white dashes + arrows that highlight yellow
    const diamMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const diamDashes = Array.from({ length: 16 }, () => {
      const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.005, 0.003), diamMat);
      scene.add(d);
      return d;
    });
    const dimArrowGeo = new THREE.ConeGeometry(0.005, 0.012, 6);
    const diamArrowTop = new THREE.Mesh(dimArrowGeo, diamMat);
    const diamArrowBottom = new THREE.Mesh(dimArrowGeo, diamMat);
    scene.add(diamArrowTop);
    scene.add(diamArrowBottom);
    diamLineRef.current = { dashes: diamDashes, arrowTop: diamArrowTop, arrowBottom: diamArrowBottom, mat: diamMat };

    // Piping rings (left + right circular end caps)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingLeft = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingRight = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingLeft.visible = false;
    pipingRight.visible = false;
    scene.add(pipingLeft);
    scene.add(pipingRight);
    pipingLeftRef.current = pipingLeft;
    pipingRightRef.current = pipingRight;

    const targetCamPos = new THREE.Vector3();

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = diamLineRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });
        const __zr = zipRef.current;
        if (__zr) {
          if (__zr.tube) __zr.tube.visible = false;
          if (__zr.teeth) __zr.teeth.forEach(t => t.visible = false);
          if (__zr.pull) __zr.pull.visible = false;
        }
      }
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) { renderer.render(scene, camera); return; }

      // Lerp dimensions (only rebuild when changed)
      const dimChanged = Math.abs(cd.r - td.r) > 0.0001 || Math.abs(cd.l - td.l) > 0.0001;
      if (dimChanged) {
        cd.r = THREE.MathUtils.lerp(cd.r, td.r, 0.1);
        cd.l = THREE.MathUtils.lerp(cd.l, td.l, 0.1);
        body.geometry.dispose();
        body.geometry = buildBolsterGeo(cd.r, cd.l);
      }
      const nextR = cd.r;
      const nextL = cd.l;

      // Position floor under cylinder
      floor.position.y = -nextR - 0.01;

      // Track piping positions with lerped dims
      if (pipingLeftRef.current && pipingLeftRef.current.visible) {
        const endRound = nextR * 0.33;
        pipingLeftRef.current.position.x = -nextL / 2 + endRound;
        pipingRightRef.current.position.x = nextL / 2 - endRound;
      }

      const hl = nextL / 2;

      // Zip — line along the bottom
      const zr = zipRef.current;
      const vis = showZipRef.current;
      const zipLen = nextL * 0.9;
      const halfZip = zipLen / 2;
      const zipY = -nextR + 0.003;
      const curvePoints = [];
      const numPts = 40;
      for (let i = 0; i <= numPts; i++) {
        const t = i / numPts;
        const x = -halfZip + t * zipLen;
        curvePoints.push(new THREE.Vector3(x, zipY, 0));
      }
      const curve = new THREE.CatmullRomCurve3(curvePoints, false);
      zr.tube.geometry.dispose();
      zr.tube.geometry = new THREE.TubeGeometry(curve, 32, 0.0025, 6, false);
      zr.tube.visible = vis;
      const toothDrop = 0.0025 + 0.001;
      for (let i = 0; i < zr.teeth.length; i++) {
        zr.teeth[i].visible = vis;
        const t = (i + 0.5) / zr.teeth.length;
        const pt = curve.getPointAt(t);
        zr.teeth[i].position.set(pt.x, pt.y - toothDrop, pt.z);
      }
      zr.pull.visible = vis;
      const pullPt = curve.getPointAt(0.97);
      zr.pull.position.set(pullPt.x, pullPt.y - toothDrop, pullPt.z);

      // Diameter dimension line — ON the left end cap face (visible from camera)
      if (diamLineRef.current) {
        const { dashes, arrowTop, arrowBottom, mat } = diamLineRef.current;
        const af = activeFieldRef.current;
        mat.color.set(af === 'diameter' ? 0xffd700 : 0xffffff);

        const lineX = -hl + 0.002; // slightly in front of left cap face
        const totalSpan = nextR * 2;
        for (let i = 0; i < dashes.length; i++) {
          const t = (i + 0.5) / dashes.length;
          const y = -nextR + totalSpan * t;
          dashes[i].position.set(lineX, y, 0);
        }
        arrowTop.position.set(lineX, nextR - 0.012, 0);
        arrowTop.rotation.set(0, 0, 0); // cone points up (+y)
        arrowBottom.position.set(lineX, -nextR + 0.012, 0);
        arrowBottom.rotation.set(0, 0, Math.PI); // cone points down (-y)
      }

      // Labels
      const { w: cw2, h: ch2 } = containerSizeRef.current;
      const af = activeFieldRef.current;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      const project = (pos) => {
        const v = pos.clone().project(camera);
        return { x: (v.x * 0.5 + 0.5) * cw2, y: (-v.y * 0.5 + 0.5) * ch2 };
      };
      const setLabel = (el, isActive, worldPos) => {
        if (!el) return;
        const p = project(worldPos);
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        el.style.opacity = '1';
        el.style.backgroundColor = isActive ? activeBg : defaultBg;
        el.style.color = isActive ? activeText : defaultText;
      };

      // Diameter label — on the LEFT end cap, next to the dimension line
      setLabel(dimLabelsRef.current.diameter, af === 'diameter',
        new THREE.Vector3(-hl - 0.02, 0, 0));

      // Length label — at the bottom center
      setLabel(dimLabelsRef.current.length, af === 'length',
        new THREE.Vector3(0, -nextR - 0.04, 0));

      // Camera
      if (resetCameraRef.current) {
        const maxDim = Math.max(td.r * 2, td.l);
        const isMobile = cw2 < 500;
        if (isMobile) {
          const distMult = 1.6;
          const distBase = 0.35;
          const camScale = 1.4;
          const dist = maxDim * distMult + distBase;
          targetCamPos.set(
            DEFAULT_CAM.x / camScale * dist,
            DEFAULT_CAM.y / camScale * dist,
            DEFAULT_CAM.z / camScale * dist
          );
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.lerp(new THREE.Vector3(0, 0, 0), 0.15);
        } else {
          // Desktop: exact user-tuned pose.
          targetCamPos.set(-0.8456, 0.1355, -0.6182);
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.lerp(new THREE.Vector3(-0.0295, -0.0244, 0.0327), 0.15);
        }
        if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
      }

      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const nw = container.clientWidth, nh = container.clientHeight;
      containerSizeRef.current = { w: nw, h: nh };
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', onResize);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { r: (diameter / NORM) / 2, l: length / NORM };
    resetCameraRef.current = true;
  }, [diameter, length]);

  useEffect(() => {
    if (bodyRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(bodyRef.current, fabricImageUrl, fabricColor, fabricZoom, () => setFabricLoading(false));
    }
  }, [fabricColor, fabricImageUrl]);

  useEffect(() => {
    if (bodyRef.current) updateTextureZoom(bodyRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings — circular tubes on the two end caps
  useEffect(() => {
    const pl = pipingLeftRef.current;
    const pr = pipingRightRef.current;
    if (!pl || !pr) return;

    const showPiping = pipingOption !== 'none';
    pl.visible = showPiping;
    pr.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (fabricColor || '#c8b89a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pl, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pr, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const r = (diameter / NORM) / 2;
    const l = length / NORM;
    const hl = l / 2;
    const endRound = r * 0.33;
    const tubeRadius = 0.003;
    const circleSegs = 64;

    // Build a circle in the YZ plane
    const circlePoints = [];
    for (let i = 0; i < circleSegs; i++) {
      const theta = (i / circleSegs) * Math.PI * 2;
      circlePoints.push(new THREE.Vector3(0, r * Math.cos(theta), r * Math.sin(theta)));
    }

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n = this.pts.length;
        const f = t * n;
        const i = Math.floor(f) % n;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n], a);
      }
    }

    const curve = new LinearClosedCurve(circlePoints);
    const tubeGeo = new THREE.TubeGeometry(curve, circleSegs, tubeRadius, 8, true);

    pl.geometry.dispose();
    pr.geometry.dispose();
    pl.geometry = tubeGeo;
    pr.geometry = tubeGeo.clone();

    // Position at the body-cap junction
    pl.position.x = -hl + endRound;
    pr.position.x = hl - endRound;
  }, [diameter, length, fabricColor, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="bolster-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.diameter = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-diameter">
        {rawDiameter > 0 ? `${unit === 'mm' ? rawDiameter * 10 : rawDiameter} ${unit}` : `D ${unit}`}
      </div>
      <div ref={el => dimLabelsRef.current.length = el} className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }} data-testid="dim-label-length">
        {rawLength > 0 ? `${unit === 'mm' ? rawLength * 10 : rawLength} ${unit}` : `L ${unit}`}
      </div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip placement</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view" data-testid="reset-view-btn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default BolsterCushion3DPreview;
